import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import axios from "axios";
import Icon from '@mdi/react';
import { mdiCalendarEdit } from '@mdi/js';
import AddressAutocomplete from './AddressAutocomplete';

function EditEventModal({ show, handleClose, onEventUpdated, event, seasonId }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "other",
    league: "",
    season: seasonId || "",
    team: "",
    sponsor: "",
    location_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
    latitude: null,
    longitude: null,
    datetime: "",
    is_public: true,
  });

  const [seasonDetails, setSeasonDetails] = useState(null);
  const [teams, setTeams] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Memoize the callback functions to prevent AddressAutocomplete re-initialization
  const handleAddressChange = useCallback((value) => {
    setFormData(prev => {
      const newData = { ...prev, address_line1: value };
      return newData;
    });
  }, []);

  const handleAddressSelect = useCallback((addressData) => {
    const locationName = addressData.displayName || addressData.formattedAddress || '';

    setFormData((prev) => ({
      ...prev,
      location_name: locationName,
      address_line1: addressData.address_line1,
      address_line2: addressData.address_line2,
      city: addressData.city,
      state: addressData.state,
      zip_code: addressData.zip_code,
      country: addressData.country,
      latitude: addressData.latitude,
      longitude: addressData.longitude,
    }));

    // Update selectedPlace for preview card
    setSelectedPlace({
      displayName: addressData.displayName || addressData.formattedAddress || '',
      formattedAddress: addressData.formattedAddress || '',
      parsedData: addressData
    });
  }, []);

  const clearLocation = useCallback(() => {
    setSelectedPlace(null);
    setFormData((prev) => ({
      ...prev,
      location_name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      latitude: null,
      longitude: null,
    }));
  }, []);

  const fetchSeasonDetails = async () => {
    try {
      const response = await axios.get(`/api/leagues/seasons/${seasonId}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.status === 200) {
        setSeasonDetails(response.data);
        // Set the league from the season details
        setFormData(prev => ({
          ...prev,
          league: response.data.league
        }));
      }
    } catch (error) {
      console.error('Error fetching season details:', error);
    }
  };

  const fetchTeams = async (seasonId) => {
    setTeamsLoading(true);
    try {
      const response = await axios.get(`/api/teams/${seasonId}/teams/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.status === 200) {
        setTeams(response.data);
      }
    } catch (error) {
      // Error fetching teams
    } finally {
      setTeamsLoading(false);
    }
  };

  const fetchSponsors = async (seasonId) => {
    setSponsorsLoading(true);
    try {
      const response = await axios.get(`/api/leagues/sponsors/?season_id=${seasonId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.status === 200) {
        setSponsors(response.data);
      }
    } catch (error) {
      // Error fetching sponsors
    } finally {
      setSponsorsLoading(false);
    }
  };

  // Initialize form data when event prop changes
  useEffect(() => {
    if (event) {
      const eventDate = new Date(event.datetime);
      const localDateTime = new Date(eventDate.getTime() - eventDate.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      setFormData({
        title: event.title || "",
        description: event.description || "",
        event_type: event.event_type || "other",
        league: event.league || "",
        season: event.season || seasonId || "",
        team: event.team || "",
        sponsor: event.sponsor?.id || "",
        location_name: event.location_name || "",
        address_line1: event.address_line1 || "",
        address_line2: event.address_line2 || "",
        city: event.city || "",
        state: event.state || "",
        zip_code: event.zip_code || "",
        country: event.country || "USA",
        latitude: event.latitude || null,
        longitude: event.longitude || null,
        datetime: localDateTime,
        is_public: event.is_public !== undefined ? event.is_public : true,
      });

      // Initialize selectedPlace for existing location data
      if (event.address_line1 || event.location_name) {
        setSelectedPlace({
          displayName: event.location_name || event.address_line1 || '',
          formattedAddress: event.location_name || event.address_line1 || '',
          parsedData: {
            address_line1: event.address_line1 || '',
            address_line2: event.address_line2 || '',
            city: event.city || '',
            state: event.state || '',
            zip_code: event.zip_code || '',
            country: event.country || 'USA',
            latitude: event.latitude || null,
            longitude: event.longitude || null,
            displayName: event.location_name || event.address_line1 || '',
            formattedAddress: event.location_name || event.address_line1 || '',
          }
        });
      } else {
        setSelectedPlace(null);
      }
    }
  }, [event, seasonId]);

  useEffect(() => {
    if (!show || !seasonId) return;
    fetchSeasonDetails();
  }, [show, seasonId]);

  useEffect(() => {
    if (seasonId) {
      fetchTeams(seasonId);
      fetchSponsors(seasonId);
    }
  }, [seasonId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessages([]);

    // Validate required fields
    const requiredFields = {
      title: 'Event Title',
      event_type: 'Event Type',
      datetime: 'Date & Time',
      address_line1: 'Event Location'
    };

    // Skip title validation for post_game events
    if (formData.event_type === 'post_game') {
      delete requiredFields.title;
    }

    const missingFields = [];
    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        missingFields.push(label);
      }
    }

    if (missingFields.length > 0) {
      setErrorMessages([`Please fill in the following required fields: ${missingFields.join(', ')}`]);
      setLoading(false);
      return;
    }

    // Format datetime for API
    const eventData = {
      ...formData,
      datetime: formData.datetime ? new Date(formData.datetime).toISOString() : null,
    };

    // Handle empty sponsor value
    if (!eventData.sponsor || eventData.sponsor === "") {
      eventData.sponsor = null;
    }

    // For post-game events, send a placeholder title that will be replaced by the backend
    if (formData.event_type === 'post_game') {
      eventData.title = 'Post-Game Social - Title will be auto-generated';
    }

    axios
      .put(`/api/leagues/events/${event.id}/`, eventData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        onEventUpdated(response.data);
        handleClose();
      })
      .catch((error) => {
        const data = error.response?.data || {};

        if (typeof data === 'object') {
          const parts = Object.entries(data).flatMap(([field, msgs]) =>
            (Array.isArray(msgs) ? msgs : [msgs]).map(m =>
              `Field [${field}]: ${m}`
            )
          );
          setErrorMessages(parts);
        } else {
          setErrorMessages(["An unexpected error occurred. Please try again."]);
        }
      })
      .finally(() => setLoading(false));
  };

  if (!event) return null;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Icon path={mdiCalendarEdit} size={1} className="me-2" />
          Edit Event
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessages.map((err, i) => (
          <div key={i} className="text-danger mb-2">{err}</div>
        ))}
        <Form onSubmit={handleSubmit} noValidate>
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Event Type *</Form.Label>
                <Form.Select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  required
                >
                  <option value="other">Other</option>
                  <option value="post_game">Post-Game Social</option>
                  <option value="team_meet">Team Meeting</option>
                  <option value="info_meet">Info Session</option>
                  <option value="league_party">League Party</option>
                  <option value="tournament">Tournament</option>
                  <option value="practice">Practice</option>
                  <option value="fundraiser">Fundraiser</option>
                  <option value="volunteer">Volunteer Event</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Event Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder={formData.event_type === 'post_game' ? 'Title will be auto-generated' : 'Enter event title'}
                  required={formData.event_type !== 'post_game'}
                  readOnly={formData.event_type === 'post_game'}
                  className={formData.event_type === 'post_game' ? 'bg-light' : ''}
                />
                {formData.event_type === 'post_game' && (
                  <Form.Text className="text-muted">
                    Title will be automatically generated based on the date and location.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter event description"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Team (Optional)</Form.Label>
                <Form.Select
                  name="team"
                  value={formData.team}
                  onChange={handleChange}
                  disabled={teamsLoading}
                >
                  <option value="">All Teams</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Form.Select>
                {teamsLoading && <Spinner animation="border" size="sm" className="mt-1" />}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Sponsor (Optional)</Form.Label>
                <Form.Select
                  name="sponsor"
                  value={formData.sponsor}
                  onChange={handleChange}
                  disabled={sponsorsLoading}
                >
                  <option value="">No Sponsor</option>
                  {sponsors.map((sponsor) => (
                    <option key={sponsor.id} value={sponsor.id}>
                      {sponsor.name}
                    </option>
                  ))}
                </Form.Select>
                {sponsorsLoading && <Spinner animation="border" size="sm" className="mt-1" />}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Date & Time *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="datetime"
                  value={formData.datetime}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Public Event</Form.Label>
                <div className="mt-2">
                  <Form.Check
                    type="checkbox"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleChange}
                    label="Make this event public"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Event Location *</Form.Label>
                <AddressAutocomplete
                  value={formData.address_line1}
                  onChange={handleAddressChange}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Search for event location..."
                  showPreview={false}
                />
                <Form.Text className="text-muted">
                  Search for an address and select it from the dropdown. The location name and address details will be parsed when you save the event.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Custom preview card for existing location data */}
          {selectedPlace && (
            <Row className="mb-3">
              <Col>
                <div className="card border-0 shadow-sm" style={{
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: '12px'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1 d-flex align-items-start">
                        {/* Location Icon */}
                        <div className="me-3 mt-1">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="text-primary"
                            style={{ minWidth: '20px' }}
                          >
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                          </svg>
                        </div>

                        {/* Location Details */}
                        <div className="flex-grow-1">
                          {/* Location Name (Display Name) */}
                          {selectedPlace.displayName && (
                            <h6 className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
                              {selectedPlace.displayName}
                            </h6>
                          )}

                          {/* Street Address */}
                          {selectedPlace.parsedData && selectedPlace.parsedData.address_line1 && (
                            <div className="text-muted" style={{ fontSize: '0.875rem', lineHeight: '1.4' }}>
                              {selectedPlace.parsedData.address_line1}
                              {selectedPlace.parsedData.address_line2 && (
                                <span>, {selectedPlace.parsedData.address_line2}</span>
                              )}
                              {selectedPlace.parsedData.city && (
                                <span>, {selectedPlace.parsedData.city}</span>
                              )}
                              {selectedPlace.parsedData.state && (
                                <span>, {selectedPlace.parsedData.state}</span>
                              )}
                              {selectedPlace.parsedData.zip_code && (
                                <span> {selectedPlace.parsedData.zip_code}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Modern Close Button */}
                      <button
                        type="button"
                        className="btn btn-link p-0 text-muted"
                        onClick={clearLocation}
                        title="Clear location"
                        style={{
                          border: 'none',
                          background: 'none',
                          fontSize: '1.2rem',
                          lineHeight: '1',
                          padding: '4px',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease-in-out'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.color = '#dc3545';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#6c757d';
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Updating...
            </>
          ) : (
            'Update Event'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditEventModal; 